import React from "react";
import {
  PieChart, Pie, Cell, ResponsiveContainer,
} from "recharts";
import ThinScroll from "../../shared/ThinScroll";
import ThemedChartTooltip from "../../shared/ThemedChartTooltip";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import { getCiReportInventoryConcentration } from "../../../api/competitiveIntelligence";
import { PanelTakeaway, fmtMoneyLocale, fmtRate, fmtPct, CATEGORY_PALETTE, categoryLabel } from "../shared";

const InventoryConcentrationBody = ({ rows, scoped }) => {
  if (!rows.length) {
    return <PanelEmpty message="No inventory concentration data yet." />;
  }

  const totalRevenue = rows.reduce((sum, row) => sum + (row.potential_revenue ?? 0), 0);

  // Share is derived from `totalRevenue` (the rows actually rendered) rather than
  // read off the backend's `revenue_concentration_pct`.
  //
  // Why: `revenue_concentration_pct` is computed CLIENT-WIDE by the RPC, which has
  // no p_category_code (backend gap #8). Once rows are scoped to a category
  // client-side, those percentages describe a larger population than the table
  // shows — on a 60/40 RV/lodging client, filtering to RV made the column sum to
  // ~60% while the footer read a hardcoded "100.0%": the table contradicted its
  // own total. Re-basing on the rendered rows keeps column and footer consistent
  // under any filter, and is identity-preserving when nothing is filtered
  // ("All" categories), since the backend already bases its own figure on the
  // full set. The dollar values are untouched — only the share basis changes.
  const shareOf = (row) => (totalRevenue > 0
    ? ((row.potential_revenue ?? 0) / totalRevenue) * 100
    : null);

  // Floor tiny slices to MIN_SLICE_PCT of the total so they're still clickable/visible.
  // The displayed value (chartValue) only affects slice geometry — tooltip shows the real value
  // via realValue, and the table is unaffected. Without this, a 0.5% slice is essentially invisible.
  const MIN_SLICE_PCT = 0.02;
  const minSliceValue = totalRevenue * MIN_SLICE_PCT;
  const chartData = rows.map((row, index) => {
    const realValue = row.potential_revenue ?? 0;
    return {
      name: row.site_type || "—",
      order: index,
      chartValue: realValue > 0 && realValue < minSliceValue ? minSliceValue : realValue,
      realValue,
      fill: CATEGORY_PALETTE[index % CATEGORY_PALETTE.length],
    };
  });
  const hasFlooredSlice = chartData.some((datum) => datum.chartValue !== datum.realValue);

  return (
    <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="flex flex-col gap-3">
        <div style={{ width: "100%", height: 280 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="chartValue"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
            >
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Pie>
            {/* Tooltip shows the real value, not the floored chart value, so users always
                see accurate dollars even on slices we padded for visibility. */}
            <ThemedChartTooltip formatter={(_v, _name, item) => fmtMoneyLocale(item?.payload?.realValue)} />
          </PieChart>
        </ResponsiveContainer>
        </div>
        {/* Legend rendered below the chart (not inside <PieChart>) so it
            can't overlay the donut, and wraps instead of overlapping. Each
            item is a color swatch + label matching the table's swatch column. */}
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-1 px-1">
          {chartData.map((entry) => (
            <div key={entry.name} className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
              <span className="inline-block w-3 h-3 rounded-sm flex-shrink-0" style={{ background: entry.fill }} />
              <span className="whitespace-nowrap">{entry.name}</span>
            </div>
          ))}
        </div>
        {hasFlooredSlice && (
          <div className="text-[11px] text-gray-500 dark:text-gray-400 text-center mt-3 px-3">
            Slices below 2% are padded for visibility — see the table for exact shares.
          </div>
        )}
        {scoped && (
          // Be explicit that shares are relative to the filtered category, not the
          // whole property — otherwise a reader could carry these numbers over to
          // the client-wide figures elsewhere on the tab.
          <div className="text-[11px] text-gray-500 dark:text-gray-400 text-center mt-2 px-3">
            Shares are relative to the selected category.
          </div>
        )}
      </div>

      <ThinScroll className="responsive-card-table-wrapper overflow-x-auto">
        <table className="responsive-card-table w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-3 py-2.5 text-left font-semibold">Site Type</th>
              <th className="px-3 py-2.5 text-right font-semibold">Dates</th>
              <th className="px-3 py-2.5 text-right font-semibold">Avg Rate</th>
              <th className="px-3 py-2.5 text-right font-semibold">Potential Rev</th>
              <th className="px-3 py-2.5 text-right font-semibold">Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.site_type}-${index}`} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <td data-label="Site Type" className="px-3 py-2.5 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-sm" style={{ background: CATEGORY_PALETTE[index % CATEGORY_PALETTE.length] }} />
                    <span className="font-medium text-gray-900 dark:text-gray-100">{row.site_type || "—"}</span>
                  </div>
                </td>
                <td data-label="Dates" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.dates_available ?? 0}</td>
                <td data-label="Avg Rate" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtRate(row.avg_price)}</td>
                <td data-label="Potential Rev" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 font-semibold whitespace-nowrap">{fmtMoneyLocale(row.potential_revenue)}</td>
                <td data-label="Share" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 font-semibold whitespace-nowrap">{fmtPct(shareOf(row))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-gray-200 dark:border-gray-700">
            <tr>
              <td className="px-3 py-2.5 font-semibold text-gray-900 dark:text-gray-100" colSpan={3}>Total</td>
              <td data-label="Potential Rev" className="px-3 py-2.5 text-right font-bold text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtMoneyLocale(totalRevenue)}</td>
              <td data-label="Share" className="px-3 py-2.5 text-right font-bold text-gray-900 dark:text-gray-100 whitespace-nowrap">100.0%</td>
            </tr>
          </tfoot>
        </table>
      </ThinScroll>
    </div>
  );
};

const InventoryConcentrationPanel = ({ clientId, categoryCode, categoryGroupNames, say }) => (
  // The ci_report_inventory_concentration RPC has no category parameter — it
  // returns a single client-wide payload keyed by normalized site type (e.g.
  // Full Hookup / Pull-Through / Back-In for RV, Airbnb / Hotel for lodging).
  // Until the backend adds p_category_code (see "Competitive Intelligence —
  // Backend Gaps.md"), scope it client-side: when a specific category is
  // selected, keep only rows whose site type belongs to that category
  // (categoryGroupNames, from ci_get_available_filters). `null` categoryCode =
  // "All", where the whole client-wide rollup is correct. If the filter leaves
  // no rows (e.g. Lodging for an RV-only client), show a notice rather than an
  // empty chart or mismatched data.
  <AnalysisPanel
    id={`inventory-concentration-${clientId}`}
    title="Inventory Concentration"
    tip="Share of potential revenue per site type. A few site types usually generate the bulk of revenue — those are the ones to price most carefully."
    description="Revenue share by site type — where your potential revenue clusters."
    fetcher={() => getCiReportInventoryConcentration(clientId)}
    deps={[clientId]}
  >
    {({ data }) => {
      const all = Array.isArray(data) ? data : [];
      const scoped = categoryCode && Array.isArray(categoryGroupNames)
        ? all.filter((row) => categoryGroupNames.includes(row.site_type))
        : all;
      if (categoryCode && scoped.length === 0) {
        return (
          <PanelEmpty
            message={`No ${categoryLabel(categoryCode)} inventory to concentrate — this client has no ${categoryLabel(categoryCode)} site types.`}
          />
        );
      }
      // Sort by potential_revenue — the same basis the Share column is now derived
      // from — so the largest share always sorts first. Sorting on the backend's
      // client-wide revenue_concentration_pct could disagree with the rendered
      // shares once rows are scoped (rounding aside, the two orderings match only
      // when both are based on the same population).
      const rows = [...scoped].sort((rowA, rowB) => (rowB.potential_revenue ?? 0) - (rowA.potential_revenue ?? 0));
      return (
        <>
          <PanelTakeaway panelKey="inventory_concentration" say={say} />
          <InventoryConcentrationBody rows={rows} scoped={Boolean(categoryCode)} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default InventoryConcentrationPanel;
